import { SupabaseClient } from "@supabase/supabase-js";
import { logException, SentryFeatureTags } from "@punchup/sentry";
import dayjs from "@punchup/utils/dayjs";
import { Database } from "../../supabase.types";
import type {
  SupportAnswerFeedbackInsert,
  SupportAnswerRating,
  SupportConversationStatus,
  SupportMessageRow,
} from "../../models/support";

/** Ratings on answers and the status of conversations (resolved / escalated). */

type Client = SupabaseClient<Database>;

function fail(
  action: string,
  error: unknown,
  extra?: Record<string, unknown>
): never {
  logException(error, {
    tags: { feature: SentryFeatureTags.SupportAssistant, action },
    extra,
  });
  throw error;
}

export async function getSupportMessage(
  supabase: Client,
  messageId: string
): Promise<Pick<SupportMessageRow, "id" | "conversation_id" | "kind"> | null> {
  const { data, error } = await supabase
    .from("support_messages")
    .select("id, conversation_id, kind")
    .eq("id", messageId)
    .maybeSingle();

  if (error) fail("getSupportMessage", error, { messageId });
  return data;
}

/** One rating per person per answer: rating again replaces it. */
export async function upsertSupportAnswerFeedback(
  supabase: Client,
  feedback: SupportAnswerFeedbackInsert
): Promise<void> {
  const { error } = await supabase
    .from("support_answer_feedback")
    .upsert(feedback, { onConflict: "message_id,user_id" });

  // Ids and the rating only: the comment is free text people type, and it
  // stays out of error reports.
  if (error) {
    fail("upsertSupportAnswerFeedback", error, {
      messageId: feedback.message_id,
      userId: feedback.user_id,
      rating: feedback.rating,
    });
  }
}

/** The ratings one person gave, by answer id. */
export async function getSupportAnswerRatings(
  supabase: Client,
  options: { userId: string; messageIds: string[] }
): Promise<Map<string, SupportAnswerRating>> {
  if (options.messageIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from("support_answer_feedback")
    .select("message_id, rating")
    .eq("user_id", options.userId)
    .in("message_id", options.messageIds);

  if (error) fail("getSupportAnswerRatings", error, options);
  return new Map(
    (data ?? []).map((row) => [
      row.message_id,
      row.rating as SupportAnswerRating,
    ])
  );
}

/** Returns false when there is no such conversation. */
export async function setSupportConversationStatus(
  supabase: Client,
  conversationId: string,
  change: { status: SupportConversationStatus; escalationNote?: string }
): Promise<boolean> {
  const { data, error } = await supabase
    .from("support_conversations")
    .update({
      status: change.status,
      status_changed_at: dayjs.utc().toISOString(),
      ...(change.escalationNote !== undefined
        ? { escalation_note: change.escalationNote }
        : {}),
    })
    .eq("id", conversationId)
    .select("id");

  if (error) fail("setSupportConversationStatus", error, { conversationId });
  return (data ?? []).length > 0;
}
